import { createTheme, ThemeOptions } from '@mui/material/styles';
import { useMemo } from 'react';
import '@fontsource/poppins'; // Defaults to weight 400
import '@fontsource/poppins/600.css'; // Weight 600 for semibold

const useCustomTheme = (theme: 'light' | 'dark') => {
    return useMemo<ThemeOptions>(
        () =>
            createTheme({
                breakpoints: {
                    keys: ['xs', 'sm', 'md', 'lg', 'xl'],
                    values: {
                        xs: 0,
                        sm: 600,
                        md: 900,
                        lg: 1200,
                        xl: 1536,
                    },
                },
                palette: {
                    mode: theme,
                    primary: {
                        main: '#1428A0', // Primary color
                    },
                    text: {
                        primary: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Main text color
                        secondary: theme === 'dark' ? '#B0BEC5' : '#4D545F', // Secondary text color
                    },
                    background: {
                        default: theme === 'dark' ? '#121212' : '#FFFFFF', // Adjust default background
                        paper: theme === 'dark' ? '#1E1E1E' : '#F4F6F8', // Adjust paper background
                    },
                    dashboard: {
                        bg: theme === 'dark' ? '#1E1E1E' : '#F9F9FC', // Dashboard background
                        cardBg: theme === 'dark' ? '#303030' : '#E2E5ED', // Card background
                        cardBtn: '#1428A0', // Button color
                        cardValue: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Card values color
                        cardTitle: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Card titles color
                        cardTitleMega: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Mega titles color
                        divider: '#D1D5DB', // Divider color
                        forecastKeyTitle: '#ED264C', // Forecast key title color
                        projectionBg: '#59E3F0', // Projection background
                        popupBg: '#FFFFFF', // Popup background
                        popupBgWithOpacity: 'rgba(255, 255, 255, 0.9)', // Translucent popup background
                        title: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Title color
                        subTitle: theme === 'dark' ? '#B0BEC5' : '#4D545F', // Subtitle color
                        white: '#FFFFFF', // Pure white
                    },
                },
                typography: {
                    fontFamily: 'Poppins, sans-serif',
                    fontWeightLight: 300,
                    fontWeightRegular: 400,
                    fontWeightMedium: 600,
                    h1: {
                        fontSize: '32px',
                        lineHeight: '42px',
                        letterSpacing: '0',
                        fontWeight: 600,
                        color: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Heading color
                    },
                    h2: {
                        fontSize: '24px',
                        lineHeight: '27px',
                        letterSpacing: '0',
                        fontWeight: 600,
                        color: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Heading color
                    },
                    h3: {
                        fontSize: '20px',
                        lineHeight: '22px',
                        letterSpacing: '0',
                        fontWeight: 600,
                        color: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Heading color
                    },
                    body1: {
                        fontSize: '16px',
                        lineHeight: '23px',
                        letterSpacing: '0',
                        fontWeight: 400,
                        color: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Body text color
                    },
                    body2: {
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0',
                        fontWeight: 400,
                        color: theme === 'dark' ? '#FFFFFF' : '#0C1015', // Body text color
                    },
                    caption: {
                        fontSize: '12px',
                        lineHeight: '17px',
                        letterSpacing: '0.6px',
                        fontWeight: 400,
                        color: theme === 'dark' ? '#B0BEC5' : '#6D6F72', // Caption color
                    },
                    overline: {
                        fontSize: '10px',
                        lineHeight: '14px',
                        letterSpacing: '0.5px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: theme === 'dark' ? '#B0BEC5' : '#4D545F', // Overline color
                    },
                },
                components: {
                    MuiButton: {
                        defaultProps: {
                            disableRipple: true,
                            disableElevation: true,
                            variant: "contained",
                        },
                        styleOverrides: {
                            root: {
                                backgroundColor: '#1428A0', // Button color
                                color: '#FFFFFF', // Button text color
                                borderRadius: "5px",
                                margin: "0.5rem",
                            }
                        }
                    },
                    MuiCheckbox: {
                        defaultProps: {
                            disableRipple: true,
                        }
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                "& .MuiInputLabel-root": {
                                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)', // Label color
                                    fontSize: "0.875rem"
                                },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 4,
                                    "& fieldset": { borderColor: "#ccc" },
                                    "&:hover fieldset": { borderColor: "#3f51b5" },
                                    "&.Mui-focused fieldset": { borderColor: "#3f51b5" },
                                },
                            },
                        },
                    },
                    MuiListItemIcon: {
                        styleOverrides: {
                            root: {
                                fontSize: '1.2rem',
                                color: "#FFF",
                            },
                        },
                    },
                    MuiListItemText: {
                        styleOverrides: {
                            root: {
                                fontSize: '1rem ',
                                color: "#FFF",
                            },
                        },
                    },

                    MuiCssBaseline: {
                        styleOverrides: {
                            '*': {
                                '&::-webkit-scrollbar': {
                                    width: '16px', // Increased width of the scrollbar
                                    height: '16px', // Increased height of the scrollbar for horizontal scrolling
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: '#f0f0f0', // Lighter background for the scrollbar track
                                    borderRadius: '10px', // Round edges for the track
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#888', // Color of the scrollbar thumb
                                    borderRadius: '10px', // Round edges for the thumb
                                    border: '3px solid #f0f0f0', // Optional: adds a border to create a separation effect
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: '#555', // Color of the scrollbar thumb on hover
                                },
                            },
                        },
                    },




                }
            }),
        [theme]
    );
};

export default useCustomTheme;
